import { GameSettingsPatchSchema, WorldGenerationSchema } from './world-settings.js';
// ワールドのテンプレート。SPEC 11.3。
import { z } from 'zod';
import { ModpackIdSchema } from './modpack.js';
import { JvmSettingsSchema, withJvmDefaults } from './jvm.js';
import { WorldWeightSchema, NodeNameSchema } from './nodes.js';
import { CreateServerSchema, ResourceSpecSchema, SoftwareSpecSchema } from './domain.js';
import { ScheduledTaskInputSchema } from './maintenance.js';
import { AddonSourceNameSchema } from './mods.js';

export const TemplateModSchema = z
  .object({
    kind: z.enum(['mod', 'plugin']).optional(),
    project: z.string().min(1).max(100),
    version: z.string().max(100).optional(),
    title: z.string().max(200),
    source: AddonSourceNameSchema.default('modrinth'),
  })
  .strict();

const PropertyKey = z.string().regex(/^[a-z0-9][a-z0-9.-]{0,63}$/);
/** テンプレートからは変えさせない server.properties のキー（agent が管理する） */
export const ManagedPropertyKeys = [
  'level-name',
  'enable-rcon',
  'rcon.port',
  'rcon.password',
  'server-ip',
  'server-port',
  'online-mode',
];

export const TemplateInputSchema = z
  .object({
    name: z.string().min(1).max(60),
    description: z.string().max(300).optional(),
    software: SoftwareSpecSchema,
    resources: ResourceSpecSchema,
    jvm: JvmSettingsSchema.optional(),
    mods: z.array(TemplateModSchema).max(100).default([]),
    gameSettings: GameSettingsPatchSchema.optional(),
    properties: z
      .record(
        PropertyKey,
        z
          .string()
          .max(200)
          .refine((v) => !/[\r\n\0]/.test(v), '改行・NUL は指定できません'),
      )
      .default({})
      .refine((p) => !Object.keys(p).some((k) => ManagedPropertyKeys.includes(k)), {
        message: `次のキーは指定できません: ${ManagedPropertyKeys.join(', ')}`,
      }),
    weight: WorldWeightSchema.default('heavy'),
    preferredNode: NodeNameSchema.optional(),
    proxied: z.boolean().default(false),
    /** 作成したサーバーに最初から入れるスケジュール */
    tasks: z.array(ScheduledTaskInputSchema).max(10).default([]),
  })
  .strict();
export type TemplateInput = z.infer<typeof TemplateInputSchema>;

const TemplateRecordSchema = TemplateInputSchema.safeExtend({
  revision: z.string().optional(),
  id: z.string(),
  builtin: z.boolean(),
  updatedAt: z.string(),
});
export type Template = z.infer<typeof TemplateRecordSchema>;
export const TemplateSchema = TemplateRecordSchema.transform((template): Template => withJvmDefaults(template));

/** 作成リクエストの params（テンプレート指定を許す） */
export const CreateServerRequestSchema = CreateServerSchema.extend({
  gameSettings: GameSettingsPatchSchema.optional(),
  worldGeneration: WorldGenerationSchema.optional(),
  templateId: z.string().min(1).max(100).optional(),
  modpackId: ModpackIdSchema.optional(),
  /** バニラ互換で hub 配下でないサーバーのクライアント Mod テンプレート。省略はその版の既定、null は「なし」 */
  clientTemplateId: z.string().min(1).max(100).nullable().optional(),
}).strict();

export const builtinTemplates: Array<z.input<typeof TemplateInputSchema> & { id: string }> = [
  {
    id: 'paper-hub',
    weight: 'light',
    name: 'Paper ハブ',
    description: 'ロビー用。軽い設定と小さめのリソース。Velocity 配下で hub として使う。',
    software: { family: 'paper', mcVersion: '1.21.10' },
    resources: { cores: 2, memoryMb: 6144, heapMb: 4096, dataDiskGb: 30 },
    mods: [],
    properties: { 'view-distance': '6', 'simulation-distance': '4', gamemode: 'adventure', 'spawn-protection': '0' },
    proxied: true,
  },
  {
    id: 'paper-survival',
    name: 'Paper サバイバル',
    description: 'プラグインで遊ぶ標準的なサバイバル。Velocity 配下。',
    software: { family: 'paper', mcVersion: '1.21.10' },
    resources: { cores: 4, memoryMb: 12288, heapMb: 9216, dataDiskGb: 60 },
    mods: [],
    properties: { difficulty: 'normal', 'view-distance': '10' },
    proxied: true,
  },
  {
    id: 'fabric-modded',
    name: 'Fabric 最適化セット',
    description: 'Fabric API と軽量化 Mod（Lithium・FerriteCore）を入れた Mod サーバーのひな形。直接接続。',
    software: { family: 'fabric', mcVersion: '1.21.10' },
    resources: { cores: 6, memoryMb: 20480, heapMb: 16384, dataDiskGb: 100 },
    mods: [
      { project: 'fabric-api', title: 'Fabric API' },
      { project: 'lithium', title: 'Lithium' },
      { project: 'ferrite-core', title: 'FerriteCore' },
    ],
    properties: { difficulty: 'normal' },
    proxied: false,
    // Mod 入りは長時間動かすとメモリが増えやすいので、毎朝 5 時に再起動する
    tasks: [
      {
        name: '毎朝の再起動',
        enabled: true,
        kind: 'restart',
        time: '05:00',
        days: [],
        commands: [],
        warnMinutes: [10, 5, 1],
        skipIfPlayersOnline: false,
        keep: 7,
      },
    ],
  },
  {
    id: 'vanilla',
    name: 'バニラ',
    description: '公式サーバーそのまま。直接接続。',
    software: { family: 'vanilla', mcVersion: '1.21.10' },
    resources: { cores: 4, memoryMb: 8192, heapMb: 6144, dataDiskGb: 40 },
    mods: [],
    properties: {},
    proxied: false,
  },
];
